import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Snackbar,
  TextField,
  Typography,
  Paper,
  alpha,
  Stack,
  useTheme,
  Alert
} from "@mui/material";
import axios from "axios";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import {
  Edit as EditIcon,
  Dashboard as DashboardIcon,
  Save as SaveIcon,
  Upload as UploadIcon,
  ArrowBack as ArrowBackIcon
} from "@mui/icons-material";
import { tokens } from "../../theme";
import { authOnlyHeaders, siteCmsUrls } from "../../api/siteCms";
import { uploadCmsImageToS3 } from "../../utils/s3ImageUpload";

const EditAboutUs = () => {
  const theme = useTheme();
  const baseColors = tokens(theme.palette.mode);

  // Using the same color scheme as in the AboutUsView component
  const colors = {
    ...baseColors,
    blueAccent: {
      100: "#d9eefb",
      200: "#b3ddf7",
      300: "#8dcbf4",
      400: "#67baf0",
      500: "#22a3f1",
      600: "#1b82c1",
      700: "#146291",
      800: "#0e4160",
      900: "#072030",
    },
  };

  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const [imagePreview, setImagePreview] = useState(null);
  const navigate = useNavigate();

  const validationSchema = Yup.object({
    title1: Yup.string().required("Title 1 is required"),
    title2: Yup.string().required("Title 2 is required"),
    description: Yup.string().required("Description is required"),
    image: Yup.mixed()
      .test("fileSize", "The file is too large (max 2MB)", (value) =>
        value ? value.size <= 1024 * 1024 * 2 : true
      )
      .test("fileType", "Unsupported file format (jpg/png only)", (value) =>
        value ? ["image/jpeg", "image/png"].includes(value.type) : true
      ),
  });

  const formik = useFormik({
    initialValues: {
      title1: "",
      title2: "",
      description: "",
      image: null,
      imageURL: ""
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setSubmitting(true);
      try {
        const payload = {
          title1: values.title1,
          title2: values.title2,
          description: values.description,
        };
        if (values.image) {
          payload.imageURL = await uploadCmsImageToS3(values.image);
        }

        await axios.put(siteCmsUrls.about, payload, {
          headers: authOnlyHeaders(),
        });
        setSnackbarMessage("About Us details updated successfully");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setTimeout(() => {
          navigate("/about");
        }, 2000);
      } catch (err) {
        setError(err);
        setSnackbarMessage("Error updating About Us details");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const fetchAboutUsDetails = async () => {
      try {
        const response = await axios.get(siteCmsUrls.about);
        if (response.data.status) {
          formik.setValues({
            title1: response.data.data.title1,
            title2: response.data.data.title2,
            description: response.data.data.description,
            imageURL: response.data.data.imageURL,
            image: null
          });
          setImagePreview(response.data.data.imageURL);
        } else {
          throw new Error(response.data.message);
        }
      } catch (err) {
        setError(err);
        setSnackbarMessage("Error fetching About Us details");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };

    fetchAboutUsDetails();
  }, [id]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleImageChange = (event) => {
    const file = event.currentTarget.files[0];
    formik.setFieldValue("image", file);

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCancel = () => {
    navigate("/about");
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px" height="80vh" overflow="auto" paddingRight="20px">
      {/* Header Section - Styled similarly to the AboutUsView header */}
      <Box
        mb={3}
        p={3}
        borderRadius={2}
        sx={{
          background: `linear-gradient(90deg, ${colors.blueAccent[500]} 0%, ${colors.blueAccent[400]} 100%)`,
          boxShadow: `0 4px 12px rgba(0, 0, 0, 0.1)`,
          border: `1px solid rgba(255, 255, 255, 0.3)`,
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <EditIcon sx={{ color: "white", fontSize: 28 }} />
          <Typography variant="h3" fontWeight="bold" color="white">
            Edit About Us
          </Typography>
        </Box>
        <Typography variant="body1" color="white">
          Update your company information and details
        </Typography>
      </Box>

      <form onSubmit={formik.handleSubmit} encType="multipart/form-data">
        <Grid container spacing={3}>
          {/* Titles and Description Section */}
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 3,
                backgroundColor: "#ffffff",
                boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
                border: `1px solid #e0e0e0`,
                borderRadius: 2,
                transition: "transform 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-4px)",
                  backgroundColor: "#ffffff",
                },
              }}
            >
              <Typography
                variant="h5"
                fontWeight="bold"
                color="#333333"
                mb={3}
              >
                Company Information
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="title1"
                    name="title1"
                    label="Main Title"
                    value={formik.values.title1}
                    onChange={formik.handleChange}
                    error={formik.touched.title1 && Boolean(formik.errors.title1)}
                    helperText={formik.touched.title1 && formik.errors.title1}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        "&:hover fieldset": {
                          borderColor: colors.blueAccent[400],
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: colors.blueAccent[500],
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="title2"
                    name="title2"
                    label="Subtitle"
                    value={formik.values.title2}
                    onChange={formik.handleChange}
                    error={formik.touched.title2 && Boolean(formik.errors.title2)}
                    helperText={formik.touched.title2 && formik.errors.title2}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        "&:hover fieldset": {
                          borderColor: colors.blueAccent[400],
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: colors.blueAccent[500],
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="description"
                    name="description"
                    label="Company Description"
                    multiline
                    rows={6}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    error={formik.touched.description && Boolean(formik.errors.description)}
                    helperText={formik.touched.description && formik.errors.description}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        "&:hover fieldset": {
                          borderColor: colors.blueAccent[400],
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: colors.blueAccent[500],
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Image Upload Section */}
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 3,
                backgroundColor: "#ffffff",
                boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
                border: `1px solid #e0e0e0`,
                borderRadius: 2,
                transition: "transform 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-4px)",
                  backgroundColor: "#ffffff",
                },
              }}
            >
              <Typography
                variant="h5"
                fontWeight="bold"
                color="#333333"
                mb={3}
              >
                About Us Image
              </Typography>
              
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: 2,
                  border: `1px dashed ${alpha(colors.blueAccent[500], 0.5)}`,
                  borderRadius: 1,
                  backgroundColor: alpha(colors.blueAccent[100], 0.1),
                }}
              >
                {imagePreview && (
                  <Box mb={3}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                )}
                
                <input
                  accept="image/*"
                  type="file"
                  id="image"
                  name="image"
                  onChange={handleImageChange}
                  style={{ display: "none" }}
                />
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<UploadIcon />}
                  sx={{
                    backgroundColor: colors.blueAccent[500],
                    color: "white",
                    fontSize: "14px",
                    padding: "10px 20px",
                    "&:hover": {
                      backgroundColor: colors.blueAccent[600],
                    },
                  }}
                >
                  Upload New Image
                  <input type="file" hidden onChange={handleImageChange} />
                </Button>
                
                {formik.errors.image && formik.touched.image && (
                  <Typography color="error" mt={1}>
                    {formik.errors.image}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box mt={4} display="flex" justifyContent="center" gap={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleCancel}
            sx={{
              color: colors.blueAccent[500],
              borderColor: colors.blueAccent[500],
              fontSize: "14px",
              padding: "10px 20px",
              "&:hover": {
                borderColor: colors.blueAccent[600],
                backgroundColor: alpha(colors.blueAccent[100], 0.1),
              },
            }}
          >
            Cancel
          </Button>
          
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={submitting}
            sx={{
              backgroundColor: colors.blueAccent[500],
              color: "white",
              fontSize: "14px",
              padding: "10px 20px",
              "&:hover": {
                backgroundColor: colors.blueAccent[600],
              },
              position: "relative"
            }}
          >
            {submitting ? "Saving..." : "Save Changes"}
            {submitting && (
              <CircularProgress
                size={24}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  color: "white",
                }}
              />
            )}
          </Button>
        </Box>
      </form>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditAboutUs;